/**
 * MessageList Organism Component
 *
 * Renders a list of conversation messages with loading states,
 * thinking indicators, and proper spacing.
 */

import React from 'react';
import type { Finding, FindingAction, Message, PrototyperBuildTimeline } from '@ideoz/core';
import { MessageItem } from '../molecules/MessageItem';
import { InlineFindingsCarousel } from '../molecules/InlineFindingsCarousel';
import { timelineHasTerminalSegments } from '../molecules/PrototyperReasoningTimeline';
import ChatThinking from '../molecules/chat-thinking';

// ============================================================================
// TYPES
// ============================================================================

export interface MessageListProps {
  conversationId?: string | null;
  projectId?: string | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  isClarification: boolean;
  clarificationOptions?: string[];
  streamProgress?: { phase?: string; nodeId?: string; toolName?: string } | null;
  /** Ordered "thinking" feed for the ThinkingPanel above the assistant reply. */
  streamThoughts?: import('@ideoz/core').ThoughtStep[];
  /** True once the assistant has streamed a token this turn (drives auto-collapse). */
  hasStartedResponse?: boolean;
  /** Wall-clock ms for the reasoning phase, once frozen. */
  thinkingDurationMs?: number | null;
  isReflection: boolean;
  isRepeated: boolean;
  isReadyToCreate: boolean;
  shouldShowBanner: boolean;
  isPrototyper?: boolean;
  onPrototypeReturn?: () => void;
  onPrototyperClick?: (
    prototyperDemoLinkId?: string | number,
    // `continueBuild`: the pill's Continue button, on a build that stopped before it passed.
    // `didNotFinish`: the pill showed "Didn't finish" when it was clicked.
    meta?: { llmInteractionId?: string | null; continueBuild?: boolean; didNotFinish?: boolean }
  ) => void;
  /** Open the streamed-markdown artifact dialog for a message's inline pill. */
  onStreamedArtifactOpen?: (artifact: import('@ideoz/core').MessageArtifact) => void;
  forcedDirection?: 'rtl' | 'ltr';

  // Actions
  onSubmit: (text: string) => void;
  onFeedback?: (
    type: 'like' | 'dislike',
    messageId?: string | null,
    projectId?: string | null
  ) => void | Promise<void>;
  setIsReflection: (value: boolean) => void;
  setIsRepeated: (value: boolean) => void;
  onRetryDrawing?: (drawType: string) => void;
  isPrototypeOpen?: boolean;
  /** Shown inside assistant messages for WebContainer tool stream / build timeline. */
  streamingWorkbenchSlot?: React.ReactNode;
  /** Live build timeline (`llmInteractionId` matches the streaming assistant turn). */
  streamingWorkbenchLlmInteractionId?: string | null;
  livePrototyperBuildTimeline?: PrototyperBuildTimeline | null;
  /** True while build-stream tools are updating the live timeline. */
  isWorkbenchBuildLive?: boolean;
  /** Active WebContainer turn idle + prod build succeeded — reveal Finalizing/Done on live timeline */
  workbenchTimelineTerminalVisible?: boolean;
  /** Loaded in the shared WebContainer — highlights the active prototype pill. */
  activeSnapshotInteractionId?: string | null;
  restoreInteractionLoadingId?: string | null;
  /** Forwarded to each `MessageItem`: the turn's build stopped before it passed. */
  isPrototypeBuildUnfinished?: (llmInteractionId: string) => boolean;
  /** Forwarded to each `MessageItem`: the turn has a finished version, so its pill has a number. */
  isPrototypeVersionSettled?: (llmInteractionId: string) => boolean;
  /** Forwarded to each `MessageItem`: the turn's version number, when the caller knows it. */
  prototypeVersionNumber?: (llmInteractionId: string) => number | undefined;
  /** Forwarded to each `MessageItem`: the busy pill's text. */
  restoreLoadingLabel?: string;
  /** Label for the live build card while an automatic fix runs (P6.4b). */
  liveBuildLabel?: string;
  /** The "needs your input" card and the turn it belongs to (P6.4b). */
  prototypeFixCard?: { llmInteractionId: string; node: React.ReactNode } | null;
  /**
   * Scope-Intelligence findings rendered inline, keyed by the message id they
   * surfaced under. Only unacted findings live here — once the user acts on one
   * it moves to the Scope Intelligence panel.
   */
  inlineFindingsByMessageId?: Record<string, Finding[]>;
  /** Resolve an inline finding (Good catch / Skip / Tell me more). */
  onFindingAction?: (findingId: string, action: FindingAction) => void;
  /** Id of the finding currently resolving — only that card's actions disable. */
  resolvingFindingId?: string | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const MessageList: React.FC<MessageListProps> = ({
  conversationId,
  projectId,
  messages,
  isLoading,
  isStreaming,
  isClarification,
  clarificationOptions = [],
  streamProgress = null,
  streamThoughts,
  hasStartedResponse,
  thinkingDurationMs,
  isReflection,
  isRepeated,
  isReadyToCreate,
  shouldShowBanner,
  onPrototypeReturn,
  onPrototyperClick,
  onStreamedArtifactOpen,
  onSubmit,
  onFeedback,
  setIsReflection,
  setIsRepeated,
  onRetryDrawing,
  forcedDirection,
  isPrototypeOpen = false,
  isPrototyper = false,
  streamingWorkbenchSlot,
  streamingWorkbenchLlmInteractionId = null,
  livePrototyperBuildTimeline = null,
  isWorkbenchBuildLive = false,
  workbenchTimelineTerminalVisible = false,
  activeSnapshotInteractionId = null,
  restoreInteractionLoadingId = null,
  isPrototypeBuildUnfinished,
  isPrototypeVersionSettled,
  prototypeVersionNumber,
  restoreLoadingLabel,
  liveBuildLabel,
  prototypeFixCard = null,
  inlineFindingsByMessageId = {},
  onFindingAction,
  resolvingFindingId = null,
}) => {
  // ========== Render Helpers ==========

  /**
   * Calculate minimum height for last message
   */
  const getLastMessageMinHeight = (index: number): string => {
    if (index !== messages.length - 1) return '';

    return shouldShowBanner && !isReadyToCreate
      ? 'min-h-[calc(100vh-360px)]'
      : 'min-h-[calc(100vh-300px)]';
  };

  /**
   * Render thinking indicator for last message (before message)
   */
  const renderThinkingIndicatorBefore = (index: number) => {
    const isLastMessage = index === messages.length - 1;
    if (!isLastMessage) return null;

    // Show thinking during loading
    if (isLoading) {
      return <ChatThinking messages={messages} />;
    }

    return null;
  };

  // ========== Render ==========

  let sawUserMessage = false;

  return (
    <>
      {messages.map((message, index) => {
        // Whether any user turn precedes this message — false only for a scope's
        // auto-prompt greeting (the opening message, no user turn before it). See
        // the `hasPrecedingUserMessage` prop doc on MessageItem.
        const hasPrecedingUserMessage = sawUserMessage;
        if (message.role === 'user') sawUserMessage = true;
        const isLastMessage = index === messages.length - 1;
        const assistantId = message.role === 'assistant' ? message.llmInteractionId : undefined;
        const useLiveTimeline =
          message.role === 'assistant' &&
          assistantId &&
          streamingWorkbenchLlmInteractionId !== null &&
          streamingWorkbenchLlmInteractionId !== undefined &&
          assistantId === streamingWorkbenchLlmInteractionId &&
          (!message.buildTimeline || isWorkbenchBuildLive);

        const timelineForMessage =
          message.role === 'assistant'
            ? useLiveTimeline
              ? livePrototyperBuildTimeline
              : message.buildTimeline
            : undefined;

        const showTimelineTerminalPhases =
          message.buildStatus === 'success' ||
          (Boolean(useLiveTimeline) && timelineHasTerminalSegments(livePrototyperBuildTimeline));

        return (
          // <article key={message.id || index} className={`pb-6 ${getLastMessageMinHeight(index)}`}>
          <article key={message.id || index} className={`pb-6 ${isPrototypeOpen ? 'text-sm' : ''}`}>
            {/* Thinking indicator before message */}
            {/* {renderThinkingIndicatorBefore(index)} */}
            {/* Message */}
            <MessageItem
              message={message}
              conversationId={conversationId}
              projectId={projectId}
              isLastMessage={isLastMessage}
              isReflection={isReflection}
              isRepeated={isRepeated}
              isClarification={isClarification}
              clarificationOptions={isLastMessage ? clarificationOptions : []}
              streamProgress={isLastMessage ? streamProgress : null}
              streamThoughts={isLastMessage ? streamThoughts : undefined}
              hasStartedResponse={isLastMessage ? hasStartedResponse : undefined}
              thinkingDurationMs={isLastMessage ? thinkingDurationMs : undefined}
              shouldShowBanner={shouldShowBanner}
              isReadyToCreate={isReadyToCreate}
              isStreaming={isStreaming}
              isLoading={isLoading}
              onPrototyperClick={onPrototyperClick}
              onStreamedArtifactOpen={onStreamedArtifactOpen}
              onSubmit={onSubmit}
              onFeedback={onFeedback}
              setIsReflection={setIsReflection}
              setIsRepeated={setIsRepeated}
              onRetryDrawing={onRetryDrawing}
              forcedDirection={forcedDirection}
              isPrototypeOpen={isPrototypeOpen}
              isPrototyper={isPrototyper}
              streamingWorkbenchSlot={streamingWorkbenchSlot}
              prototyperBuildTimeline={timelineForMessage ?? null}
              // `useLiveTimeline` also stays true for the streamed turn after its build
              // settles, until a history reload adds `buildTimeline`; passing it alone kept
              // that turn's prototype card hidden (FE-8). Only a build that is actually
              // running counts as live.
              isWorkbenchBuildLive={Boolean(useLiveTimeline) && isWorkbenchBuildLive}
              showPrototyperTimelineTerminalPhases={showTimelineTerminalPhases}
              activeSnapshotInteractionId={activeSnapshotInteractionId}
              restoreInteractionLoadingId={restoreInteractionLoadingId}
              isPrototypeBuildUnfinished={isPrototypeBuildUnfinished}
              isPrototypeVersionSettled={isPrototypeVersionSettled}
              prototypeVersionNumber={prototypeVersionNumber}
              restoreLoadingLabel={restoreLoadingLabel}
              hasPrecedingUserMessage={hasPrecedingUserMessage}
              liveBuildLabel={liveBuildLabel}
              prototypeFixSlot={
                prototypeFixCard && message.llmInteractionId === prototypeFixCard.llmInteractionId
                  ? prototypeFixCard.node
                  : undefined
              }
            />
            {/* Inline Scope-Intelligence findings surfaced under this message.
                Rendered only while unacted; acting on one moves it to the panel.
                Multiple findings page one-at-a-time (carousel) instead of
                stacking. */}
            {message.id &&
              onFindingAction &&
              (inlineFindingsByMessageId[message.id]?.length ?? 0) > 0 && (
                <div className="ms-8 mt-2">
                  <InlineFindingsCarousel
                    findings={inlineFindingsByMessageId[message.id]!}
                    onAction={onFindingAction}
                    resolvingFindingId={resolvingFindingId}
                  />
                </div>
              )}
            {/* Thinking indicator after message */}
            {/* {renderThinkingIndicatorAfter(index)} */}
          </article>
        );
      })}
    </>
  );
};

export default MessageList;
